import React, { useState, useEffect } from "react";
import {
  Grid,
  makeStyles,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import GeneralModal from "./GeneralModal";
import { TextFieldInput } from "../components-atoms";
import { ButtonTextPrimary, ButtonPrimary } from "../components-atoms";
import { editShopUser, EditShopUserData } from "../redux/actions/shopDetails";
import { ShopUserDetails } from "../redux/actions/shopDetails";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: ShopUserDetails | null;
  merchantId: string;
  shopId: string;
}

export const EditShopUserModal = ({
  visible,
  onClose,
  onSuccess,
  user,
  merchantId,
  shopId
}: IProps) => {
  const classes = useStyles();
  const [formData, setFormData] = useState<EditShopUserData>({
    FirstName: "",
    LastName: "",
    Email: "",
    PhoneNumber: "",
    EmailNotificationsEnabled: false,
    PhoneNotificationsEnabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        FirstName: user.FirstName || "",
        LastName: user.LastName || "",
        Email: user.Email?.Email || "",
        PhoneNumber: user.PhoneNumber?.PhoneNumber || "",
        EmailNotificationsEnabled: user.Email?.NotificationsEnabled || false,
        PhoneNotificationsEnabled: user.PhoneNumber?.NotificationsEnabled || false,
      });
    }
  }, [user]);

  const handleInputChange = (field: keyof EditShopUserData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const onFormSubmit = () => {
    if (!isFormValid() || !user) {
      return;
    }

    setLoading(true);
    setError(null);

    const requestData = {
      ...formData
    };
    
    editShopUser(
      merchantId,
      shopId,
      user.Id,
      requestData,
      (response) => {
        setLoading(false);
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      },
      (err) => {
        setLoading(false);
        
        // Handle different error structures
        let errorMessage = 'Failed to update user. Please try again.';
        
        if (typeof err === 'string') {
          errorMessage = err;
        } else if (err?.response?.data?.Error) {
          errorMessage = err.response.data.Error;
        } else if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      }
    );
  };

  const handleClose = () => {
    // Reset form data
    setFormData({
      FirstName: "",
      LastName: "",
      Email: "",
      PhoneNumber: "",
      EmailNotificationsEnabled: false,
      PhoneNotificationsEnabled: false,
    });
    setError(null);
    setLoading(false);
    onClose();
  };

  const isFormValid = () => {
    return (
      !!formData.FirstName?.trim() &&
      !!formData.LastName?.trim() &&
      !!formData.Email?.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email || "")
    );
  };

  return (
    <GeneralModal
      title="Edit Shop User"
      visible={visible}
      onClose={handleClose}
    >
      <form>
        <GeneralModal.ContentArea>
          <fieldset className={classes.fieldset}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextFieldInput
                  label="First Name"
                  value={formData.FirstName}
                  onChange={handleInputChange('FirstName')}
                  placeholder="Enter first name"
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextFieldInput
                  label="Last Name"
                  value={formData.LastName}
                  onChange={handleInputChange('LastName')}
                  placeholder="Enter last name"
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Email Address"
                  type="email"
                  value={formData.Email}
                  onChange={handleInputChange('Email')}
                  placeholder="Enter email address"
                  required
                  disabled={loading}
                  error={!!error}
                  helperText={error}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Phone Number"
                  value={formData.PhoneNumber || ""}
                  onChange={handleInputChange('PhoneNumber')}
                  placeholder="Enter phone number"
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.EmailNotificationsEnabled}
                      onChange={handleInputChange('EmailNotificationsEnabled')}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Enable Email Notifications"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.PhoneNotificationsEnabled}
                      onChange={handleInputChange('PhoneNotificationsEnabled')}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Enable Phone Notifications"
                />
              </Grid>
            </Grid>
          </fieldset>
        </GeneralModal.ContentArea>
        
        <GeneralModal.ButtonArea>
          <ButtonTextPrimary onClick={handleClose} disabled={loading}>
            Cancel
          </ButtonTextPrimary>
          <ButtonPrimary
            onClick={onFormSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? "Updating..." : "Update User"}
          </ButtonPrimary>
        </GeneralModal.ButtonArea>
      </form>
    </GeneralModal>
  );
};

const useStyles = makeStyles({
  fieldset: {
    border: "none",
    padding: 0,
    margin: 0,
  },
});

export default EditShopUserModal;
